import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  RiseOutlined,
  MoonOutlined,
  TeamOutlined,
  ArrowRightOutlined,
  BookOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined
} from "@ant-design/icons";

import { BOT_LINK } from "../../utils/platform";
import LanguageSelector from "../../components/LanguageSelector/LanguageSelector";
import PwaInstallPrompt from "../../components/PwaInstallPrompt/PwaInstallPrompt";
import { usePwaInstall } from "../../hooks/usePwaInstall";
import { installPwaFromClick } from "../../utils/pwaInstall";
import KnowledgeModal from "../../components/Knowledge/KnowledgeModal";
import KredafiLogo from "../../kredafi/KredafiLogo";
import "../../kredafi/kredafi.css";
import styles from "./Landing.module.css";

interface Props {
  onEnter: () => void;
}

const Landing: React.FC<Props> = ({ onEnter }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "ru").startsWith("ru") ? "ru" : "en";
  const isRTL = (i18n.language || "en").startsWith("ar");

  // Knowledge modal state
  const [isKnowledgeOpen, setIsKnowledgeOpen] = useState(false);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);

  const { isInstalled: pwaInstalled, platform } = usePwaInstall();

  // Theme mode: dark (default) or light (дневной)
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    if (typeof window !== "undefined") {
      return (
        (localStorage.getItem("wt_landing_theme") as "dark" | "light") || "dark"
      );
    }
    return "dark";
  });

  const toggleTheme = () => {
    const nextTheme = theme === "dark" ? "light" : "dark";
    setTheme(nextTheme);
    try {
      localStorage.setItem("wt_landing_theme", nextTheme);
    } catch {
      /* ignore */
    }
  };

  // Interactive Yield Calculator State
  const [loanAmount, setLoanAmount] = useState<number>(1000);
  const [loanDays, setLoanDays] = useState<number>(14);
  const [isInsured, setIsInsured] = useState<boolean>(true);

  const openDoc = (articleId?: string) => {
    setSelectedArticleId(articleId || null);
    setIsKnowledgeOpen(true);
  };

  // Yield calculation
  const calculatedResult = useMemo(() => {
    // Со страховкой ставка ~0.6% в сутки, без страховки до ~1.0% в сутки
    const dailyRate = isInsured ? 0.006 : 0.01;
    const profit = Math.round(loanAmount * dailyRate * loanDays);
    const totalReturn = loanAmount + profit;
    return {
      profit,
      totalReturn,
      ratePercent: (dailyRate * 100).toFixed(1),
    };
  }, [loanAmount, loanDays, isInsured]);

  return (
    <div
      className={`${styles.landingRoot} ${theme === "light" ? styles.lightTheme : ""}`}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className={styles.glowTop} />

      {/* Modern Top Navigation Bar */}
      <nav className={styles.navbar}>
        <div className={styles.navContainer}>
          <div className={styles.brand} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <KredafiLogo size={48} theme={theme} />
          </div>

          <div className={styles.navLinks}>
            <button type="button" className={styles.navLink} onClick={() => openDoc("margin-loans")}>
              {lang === "ru" ? "Маржинальные займы" : "Margin Loans"}
            </button>
            <button type="button" className={styles.navLink} onClick={() => openDoc("overnight")}>
              {lang === "ru" ? "Овернайт" : "Overnight"}
            </button>
            <button type="button" className={styles.navLink} onClick={() => openDoc("affiliates")}>
              {lang === "ru" ? "Партнерам" : "Affiliates"}
            </button>
            <button type="button" className={styles.navLink} onClick={() => openDoc("unsecured")}>
              {lang === "ru" ? "Необеспеченные займы" : "Unsecured Loans"}
            </button>
            <button
              type="button"
              className={`${styles.navLink} ${styles.navLinkAccent}`}
              onClick={() => openDoc()}
            >
              <BookOutlined /> {lang === "ru" ? "База знаний" : "Docs & FAQ"}
            </button>
          </div>

          <div className={styles.navRight}>
            <button
              type="button"
              className={styles.themeToggleBtn}
              onClick={toggleTheme}
              title={theme === "dark" ? "Переключить на дневной режим" : "Переключить на ночной режим"}
            >
              <span>{theme === "dark" ? "☀️" : "🌙"}</span>
            </button>

            <LanguageSelector theme={theme} compact={true} />

            <button type="button" className={styles.navLoginBtn} onClick={onEnter}>
              {lang === "ru" ? "Войти" : "Sign In"}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.badgePill}>
          <ThunderboltOutlined className={styles.badgeSpark} />
          <span>
            {lang === "ru"
              ? "⚡ Мультивалютная децентрализованная P2P кредитная сеть • Web3"
              : "⚡ Multichain Decentralized P2P Credit Network • Web3"}
          </span>
        </div>

        <p className={styles.heroSlogan}>
          {lang === "ru"
            ? "Кредитный пул. Вкл. Выкл. Один клик."
            : "Credit pool. On. Off. One click."}
        </p>
        <p className={styles.heroSubline}>
          {lang === "ru"
            ? "Криптозалог · плечо x3 · страховой пул 100%"
            : "Crypto collateral · x3 leverage · 100% insurance pool"}
        </p>

        <h1 className={styles.heroTitle}>
          {lang === "ru" ? (
            <>
              Социальное кредитование с{" "}
              <span className={styles.heroGradient}>маржинальным доходом</span>
            </>
          ) : (
            <>
              Next-Gen P2P Lending with{" "}
              <span className={styles.heroGradient}>Margin Leverage</span>
            </>
          )}
        </h1>

        <p className={styles.heroSubtitle}>
          {lang === "ru"
            ? "Кредитный пул ликвидности: выдача займов через пул под залог, кредитные сертификаты и поручительство. Кредитное плечо до x3, овернайт свободных остатков, страховой пул на 100% суммы и защита от Bankrun."
            : "Credit liquidity pool: loans issued through the pool backed by collateral, credit certificates, and guarantees. Up to x3 leverage, overnight yield on idle balances, 100% insurance coverage, and anti-bankrun protection."}
        </p>

        {/* Primary Action Buttons */}
        <div className={styles.heroActions}>
          <button type="button" className={styles.ctaPrimary} onClick={onEnter}>
            <ThunderboltOutlined />
            <span>{lang === "ru" ? "Открыть приложение" : "Open Web App"}</span>
          </button>

          {!pwaInstalled && platform === "android" && (
            <button type="button" className={styles.ctaPwa} onClick={installPwaFromClick}>
              <span style={{ fontSize: 16 }}>📲</span>
              <span>{lang === "ru" ? "Установить PWA" : "Install App"}</span>
            </button>
          )}

          <a
            href={BOT_LINK}
            target="_blank"
            rel="noreferrer"
            className={styles.ctaTelegram}
          >
            <svg className={styles.tgIcon} viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.19-.08-.05-.19-.02-.27 0-.12.03-1.99 1.27-5.61 3.72-.53.36-1.01.54-1.44.53-.47-.01-1.38-.27-2.05-.48-.83-.27-1.48-.42-1.42-.88.03-.24.38-.49 1.03-.75 4.04-1.76 6.74-2.92 8.09-3.48 3.85-1.6 4.65-1.88 5.17-1.89.11 0 .37.03.54.17.14.12.18.28.2.45-.02.07-.02.21-.04.36z" />
            </svg>
            <span>{lang === "ru" ? "Открыть в Telegram" : "Launch in Telegram"}</span>
          </a>
        </div>

        {/* Live Metrics Ticker */}
        <div className={styles.statsStrip}>
          <div className={styles.statItem}>
            <div className={`${styles.statValue} ${styles.statValueOrange}`}>~0.6%</div>
            <div className={styles.statLabel}>
              {lang === "ru" ? "Базовая ставка в сутки (со страховкой)" : "Daily Base Rate (Guaranteed)"}
            </div>
          </div>
          <div className={styles.statItem}>
            <div className={`${styles.statValue} ${styles.statValueBlue}`}>
              {lang === "ru" ? "до 90 дней" : "up to 90 days"}
            </div>
            <div className={styles.statLabel}>{lang === "ru" ? "Сроки P2P займов" : "Loan Maturities"}</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>10M+</div>
            <div className={styles.statLabel}>{lang === "ru" ? "Участников платформы" : "Active Community"}</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>100%</div>
            <div className={styles.statLabel}>{lang === "ru" ? "Покрытие выданного займа Страховым пулом" : "Issued Loan Coverage by Insurance Pool"}</div>
          </div>
        </div>
      </section>

      {/* Interactive Yield Calculator */}
      <section className={styles.calcSection}>
        <div className={styles.calcCard}>
          <div>
            <h2 className={styles.calcHeaderTitle}>
              {lang === "ru" ? "Интерактивный калькулятор доходности" : "Interactive Lending Calculator"}
            </h2>
            <p className={styles.calcHeaderDesc}>
              {lang === "ru"
                ? "Укажите сумму, срок размещения (до 90 дней) и режим покрытия страховым пулом."
                : "Select amount, loan term (up to 90 days), and insurance pool coverage mode."}
            </p>

            {/* Amount Slider */}
            <div className={styles.calcInputGroup}>
              <div className={styles.calcLabelRow}>
                <span>{lang === "ru" ? "Сумма займа:" : "Loan Amount:"}</span>
                <span className={styles.calcAmountValue}>${loanAmount.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="50"
                max="5000"
                step="50"
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
                className={styles.calcRange}
              />
            </div>

            {/* Duration Pills */}
            <div className={styles.calcInputGroup}>
              <div className={styles.calcLabelRow}>
                <span>{lang === "ru" ? "Срок кредитования:" : "Loan Duration:"}</span>
                <span>{loanDays} {lang === "ru" ? "дней" : "days"}</span>
              </div>
              <div className={styles.termPills}>
                {[3, 7, 14, 30, 90].map((d) => (
                  <div
                    key={d}
                    className={`${styles.termPill} ${loanDays === d ? styles.termPillActive : ""}`}
                    onClick={() => setLoanDays(d)}
                  >
                    {d} {lang === "ru" ? "дн." : "days"}
                  </div>
                ))}
              </div>
            </div>

            {/* Insurance / Guarantee Fund Toggle */}
            <div className={styles.leverageToggleBox}>
              <div>
                <div className={styles.leverageTitle}>
                  {lang === "ru"
                    ? "🛡️ Покрытие Страховым пулом (Рекомендуется)"
                    : "🛡️ Insurance Pool Coverage (Recommended)"}
                </div>
                <div className={styles.leverageDesc}>
                  {lang === "ru"
                    ? "Страховой пул полностью покрывает выданный займ при выборе опции страховки (ставка ~0.6%/сутки). Без страховки доход до 1.0%/сутки с прямым принятием риска."
                    : "The insurance pool fully covers the issued loan when the insurance option is selected (rate ~0.6%/day). Without insurance, yields reach up to 1.0%/day with direct risk."}
                </div>
              </div>
              <input
                type="checkbox"
                checked={isInsured}
                onChange={(e) => setIsInsured(e.target.checked)}
                style={{ width: 22, height: 22, cursor: "pointer", accentColor: "#0098ea" }}
              />
            </div>
          </div>

          {/* Calculator Output */}
          <div className={styles.calcResultBox}>
            <div className={styles.resultRow}>
              <span>{lang === "ru" ? "Дневная ставка:" : "Est. Daily Rate:"}</span>
              <span className={styles.resultValue}>{calculatedResult.ratePercent}% / {lang === "ru" ? "сутки" : "day"}</span>
            </div>
            <div className={styles.resultRow}>
              <span>{lang === "ru" ? "Ориентировочная прибыль:" : "Estimated Profit:"}</span>
              <span className={styles.calcProfitValue}>+${calculatedResult.profit.toLocaleString()}</span>
            </div>
            <div className={styles.resultTotalRow}>
              <span className={styles.totalProfitLabel}>{lang === "ru" ? "Итого к возврату:" : "Total Payout:"}</span>
              <span className={styles.totalProfitAmount}>${calculatedResult.totalReturn.toLocaleString()}</span>
            </div>
            <button type="button" className={styles.calcActionBtn} onClick={onEnter}>
              {lang === "ru" ? "Начать выдавать займы" : "Start Lending Now"}
            </button>
          </div>
        </div>
      </section>

      {/* Bento-Grid Features */}
      <section className={styles.bentoSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionPre}>
            {lang === "ru" ? "Инновационные инструменты" : "Next-Gen Features"}
          </div>
          <h2 className={styles.sectionTitle}>
            {lang === "ru" ? "Финансовые возможности KredaFi" : "KredaFi Financial Suite"}
          </h2>
        </div>

        <div className={styles.bentoGrid}>
          {/* Bento Card 1: Margin Loans */}
          <div className={styles.bentoCard}>
            <div>
              <div className={`${styles.bentoIconWrapper} ${styles.bentoIconOrange}`}>
                <RiseOutlined />
              </div>
              <h3 className={styles.bentoCardTitle}>
                {lang === "ru" ? "Маржинальные займы (Плечо x3)" : "Margin Loans (x3 Leverage)"}
              </h3>
              <p className={styles.bentoCardDesc}>
                {lang === "ru"
                  ? "Увеличьте потенциал ваших инвестиций до 300% от баланса или стоимости кредитного сертификата. Плавающая ставка от 0.05% в сутки со сроком от 1 до 90 дней."
                  : "Multiply your capital up to 300% of your deposit or certificate. Floating rate from 0.05% daily with terms from 1 to 90 days."}
              </p>
            </div>
            <button type="button" className={styles.bentoLinkBtn} onClick={() => openDoc("margin-loans")}>
              <span>{lang === "ru" ? "Как работают маржинальные займы" : "Learn how margin loans work"}</span>
              <ArrowRightOutlined />
            </button>
          </div>

          {/* Bento Card 2: Overnight */}
          <div className={styles.bentoCard}>
            <div>
              <div className={`${styles.bentoIconWrapper} ${styles.bentoIconBlue}`}>
                <MoonOutlined />
              </div>
              <h3 className={styles.bentoCardTitle}>
                {lang === "ru" ? "Ночные займы (Overnight)" : "Overnight Daily Returns"}
              </h3>
              <p className={styles.bentoCardDesc}>
                {lang === "ru"
                  ? "Свободные средства не лежат без дела. Автоматическое размещение свободных остатков на ночь с гарантированным начислением процентов каждое утро."
                  : "Your idle funds never sleep. Automated overnight lending distributes liquidity to borrowers and returns yield directly to your balance by morning."}
              </p>
            </div>
            <button type="button" className={styles.bentoLinkBtn} onClick={() => openDoc("overnight")}>
              <span>{lang === "ru" ? "Подробнее об овернайт" : "Learn about overnight loans"}</span>
              <ArrowRightOutlined />
            </button>
          </div>

          {/* Bento Card 3: Certificates & Score */}
          <div className={styles.bentoCard}>
            <div>
              <div className={`${styles.bentoIconWrapper} ${styles.bentoIconGreen}`}>
                <SafetyCertificateOutlined />
              </div>
              <h3 className={styles.bentoCardTitle}>
                {lang === "ru" ? "Кредитные сертификаты (NFT) & Скоринг" : "Credit Certificates (NFT) & Score"}
              </h3>
              <p className={styles.bentoCardDesc}>
                {lang === "ru"
                  ? "Токенизированные долговые обязательства, обеспеченные пулами ликвидности. Свободная передача на вторичном рынке и прозрачный кредитный рейтинг участников."
                  : "Tokenized debt claims backed by liquidity pools. Tradeable on secondary markets with transparent decentralized borrower reputation scoring."}
              </p>
            </div>
            <button type="button" className={styles.bentoLinkBtn} onClick={() => openDoc("certificates")}>
              <span>{lang === "ru" ? "Подробнее о сертификатах" : "Learn about certificates"}</span>
              <ArrowRightOutlined />
            </button>
          </div>

          {/* Bento Card 4: Affiliates & Unions */}
          <div className={styles.bentoCard}>
            <div>
              <div className={`${styles.bentoIconWrapper} ${styles.bentoIconPurple}`}>
                <TeamOutlined />
              </div>
              <h3 className={styles.bentoCardTitle}>
                {lang === "ru" ? "Партнерская сеть" : "Affiliate Network"}
              </h3>
              <p className={styles.bentoCardDesc}>
                {lang === "ru"
                  ? "Зарабатывайте бонусы за привлечение участников и получайте процент от оборота сети."
                  : "Earn multi-tier referral commissions and share in network turnover from the participants you invite."}
              </p>
            </div>
            <button type="button" className={styles.bentoLinkBtn} onClick={() => openDoc("affiliates")}>
              <span>{lang === "ru" ? "Условия партнерской программы" : "View affiliate rewards"}</span>
              <ArrowRightOutlined />
            </button>
          </div>
        </div>
      </section>

      {/* Rich 4-Column Footer */}
      <footer className={styles.richFooter}>
        <div className={styles.footerContainer}>
          <div className={styles.footerGrid}>
            {/* Col 0: Brand & Mission */}
            <div className={styles.footerBrandCol}>
              <div className={styles.brand}>
                <KredafiLogo size={52} theme={theme} />
              </div>
              <p className={styles.footerDesc}>
                {lang === "ru"
                  ? "Мультичейн криптокошелек и кредитный пул нового поколения. Выдача займов под обеспечение и кредитные сертификаты, овернайт свободных остатков и единый баланс во всех сетях (TON, Polygon, Solana, TRON, BNB, Ethereum)."
                  : "Next-generation multichain wallet and credit liquidity pool. Issue loans backed by collateral and credit certificates, earn overnight yields on idle balances, and manage unified multichain assets (TON, Polygon, Solana, TRON, BNB, Ethereum)."}
              </p>
              <div className={styles.footerBadge}>
                <SafetyCertificateOutlined /> 10% Monthly Anti-Bankrun Safety Limit
              </div>
            </div>

            {/* Col 1: Products */}
            <div>
              <div className={styles.footerColTitle}>
                {lang === "ru" ? "Кредитные продукты" : "Credit Products"}
              </div>
              <ul className={styles.footerLinksList}>
                <li>
                  <button type="button" className={styles.footerLinkBtn} onClick={() => openDoc("margin-loans")}>
                    {lang === "ru" ? "Маржинальные займы" : "Margin Loans"}
                  </button>
                </li>
                <li>
                  <button type="button" className={styles.footerLinkBtn} onClick={() => openDoc("overnight")}>
                    {lang === "ru" ? "Ночные займы (Overnight)" : "Overnight Loans"}
                  </button>
                </li>
                <li>
                  <button type="button" className={styles.footerLinkBtn} onClick={() => openDoc("unsecured")}>
                    {lang === "ru" ? "Необеспеченные займы" : "Unsecured Loans"}
                  </button>
                </li>
                <li>
                  <button type="button" className={styles.footerLinkBtn} onClick={() => openDoc("certificates")}>
                    {lang === "ru" ? "Кредитные сертификаты" : "Credit Certificates"}
                  </button>
                </li>
                <li>
                  <button type="button" className={styles.footerLinkBtn} onClick={() => openDoc("liquidity-pools")}>
                    {lang === "ru" ? "Пулы ликвидности" : "Liquidity Pools"}
                  </button>
                </li>
              </ul>
            </div>

            {/* Col 2: Ecosystem */}
            <div>
              <div className={styles.footerColTitle}>
                {lang === "ru" ? "Экосистема" : "Ecosystem"}
              </div>
              <ul className={styles.footerLinksList}>
                <li>
                  <button type="button" className={styles.footerLinkBtn} onClick={() => openDoc("score")}>
                    {lang === "ru" ? "Кредитный скоринг" : "Credit Score"}
                  </button>
                </li>
                <li>
                  <button type="button" className={styles.footerLinkBtn} onClick={() => openDoc("insurance")}>
                    {lang === "ru" ? "Страховой пул" : "Insurance Pool"}
                  </button>
                </li>
              </ul>
            </div>

            {/* Col 3: Affiliates & Community */}
            <div>
              <div className={styles.footerColTitle}>
                {lang === "ru" ? "Партнерам" : "Affiliates"}
              </div>
              <ul className={styles.footerLinksList}>
                <li>
                  <button type="button" className={styles.footerLinkBtn} onClick={() => openDoc("affiliates")}>
                    {lang === "ru" ? "Партнерская программа" : "Affiliate Network"}
                  </button>
                </li>
                <li>
                  <button type="button" className={styles.footerLinkBtn} onClick={() => openDoc()}>
                    {lang === "ru" ? "База знаний & FAQ" : "Knowledge Base"}
                  </button>
                </li>
              </ul>
            </div>

            {/* Col 4: About & Legal */}
            <div>
              <div className={styles.footerColTitle}>
                {lang === "ru" ? "О платформе" : "About & Legal"}
              </div>
              <ul className={styles.footerLinksList}>
                <li>
                  <button type="button" className={styles.footerLinkBtn} onClick={() => openDoc("about")}>
                    {lang === "ru" ? "История KredaFi" : "KredaFi Story"}
                  </button>
                </li>
                <li>
                  <a href="/privacy.html" className={styles.footerLinkBtn}>
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/terms.html" className={styles.footerLinkBtn}>
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar: Copyright, Beta Notice, and PWA prompt */}
          <div className={styles.footerBottomBar}>
            <div className={styles.copyrightCol}>
              <div className={styles.copyrightText}>
                © {new Date().getFullYear()} KredaFi. {lang === "ru" ? "Все права защищены." : "All rights reserved."}
              </div>
              <div className={styles.betaSubtext}>
                {lang === "ru"
                  ? "Платформа работает в режиме публичной бета-версии"
                  : "Platform is running in public beta mode"}
              </div>
            </div>

            <div className={styles.footerBetaBadge}>
              <span className={styles.footerBetaDot} />
              <span>{lang === "ru" ? "Бета-версия" : "Beta Version"}</span>
            </div>

            <div className={styles.footerPwaWrapper}>
              <PwaInstallPrompt theme={theme} />
            </div>
          </div>
        </div>
      </footer>

      {/* Recovered Knowledge Base Reader Modal */}
      <KnowledgeModal
        isOpen={isKnowledgeOpen}
        onClose={() => setIsKnowledgeOpen(false)}
        initialArticleId={selectedArticleId}
        theme={theme}
      />
    </div>
  );
};

export default Landing;
